/*
 * mischu-carousel
 * Scroll-snap track with prev/next controls and a segment progress
 * indicator. Purely presentational — the slides are plain links, so
 * there is no product/cart state here. Keyboard: arrow keys move
 * between slides when the track has focus. Honours prefers-reduced-motion.
 */
if (!customElements.get('mischu-carousel')) {
  customElements.define(
    'mischu-carousel',
    class MischuCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;

        this.slides = Array.from(this.track.children);
        this.prevBtn = this.querySelector('[data-carousel-prev]');
        this.nextBtn = this.querySelector('[data-carousel-next]');
        this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.prevBtn?.addEventListener('click', () => this.scrollByCards(-1));
        this.nextBtn?.addEventListener('click', () => this.scrollByCards(1));
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.scrollByCards(1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.scrollByCards(-1); }
        });

        this.onScroll = this.rafThrottle(() => this.update());
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.update();
      }

      step() {
        // One card width plus the gap, derived from the first two slides so
        // it stays correct across breakpoints.
        if (this.slides.length < 2) return this.slides[0]?.offsetWidth || this.track.clientWidth;
        return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
      }

      scrollByCards(dir) {
        this.track.scrollBy({ left: this.step() * dir, behavior: this.reduceMotion ? 'auto' : 'smooth' });
      }

      goTo(index) {
        const target = this.slides[index];
        if (!target) return;
        const delta = target.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
        this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: this.reduceMotion ? 'auto' : 'smooth' });
      }

      activeIndex() {
        const step = this.step() || 1;
        return Math.round(this.track.scrollLeft / step);
      }

      update() {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1;
        const atStart = this.track.scrollLeft <= 1;
        const atEnd = this.track.scrollLeft >= maxScroll;

        this.toggleControl(this.prevBtn, atStart);
        this.toggleControl(this.nextBtn, atEnd);

        const active = this.activeIndex();
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === active ? 'true' : 'false'));
      }

      toggleControl(btn, disabled) {
        if (!btn) return;
        btn.disabled = disabled;
        btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }

      rafThrottle(fn) {
        let ticking = false;
        return () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => { fn(); ticking = false; });
        };
      }
    }
  );
}
